import IconButton from "../../buttons/IconButton";
import { PlusIcon, DeleteIcon } from "../../Icons";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { useEffect, useState } from "react";
import { BasicNameValue } from "../../../types/types";

type ParameterPresetChildProps = {
  index: number;
  currentParameter: BasicNameValue;
  localAvailableParams: string[];
  canAdd?: boolean; // Optional prop to control the add button
  canEdit?: boolean; // Optional prop to control editability
  canEditValue?: boolean; // Optional prop to allow value-only edits
  canDelete?: boolean; // Optional prop to control delete the child element
  onAdd?: (newChild: BasicNameValue) => void;
  onAddLocalParameter?: (newParameterName: string, index: number, value?: string) => void;
  onDelete?: (index: number) => void;
};

const ParameterPresetChild: React.FC<ParameterPresetChildProps> = ({
  index, currentParameter, onAdd, onDelete,
  canAdd, onAddLocalParameter, canEdit, canEditValue, canDelete, localAvailableParams
}) => {
  const [activeParameterPreset, setActiveParameterPreset] = useState<BasicNameValue>(currentParameter);
  const [newParameterName, setNewParameterName] = useState<string>(""); // Placeholder for modifying parameter name
  const [newParameterValue, setNewParameterValue] = useState<string>(""); // Placeholder for modifying parameter value
  const isValueEditable = Boolean(canEdit || canEditValue);
  // Always sync local state with prop
  useEffect(() => {
    setActiveParameterPreset(currentParameter);
  }, [currentParameter]);

  // No local parameters state; always use localAvailableParams from parent

  return (
    <>
      {
        canEdit ? (
          <div className="col-span-5">
            <Dropdown
              value={activeParameterPreset?.name || ""}
              options={localAvailableParams || []}
              className="min-w-[100%] max-w-[100%]"
              appendTo={document.body}
              pt={{
                root: {
                  className: 'min-w-[100%] bg-transparent dark:border-neutral-70 dark:bg-transparent'
                },
                input: {
                  className: 'py-2'
                }
              }}
              onChange={(e) => {
                setActiveParameterPreset(prev => {
                  if (!prev) return prev;
                  const updatedPreset = Object.assign(
                    Object.create(Object.getPrototypeOf(prev)),
                    { ...prev, name: e.value, value: prev.value || "" }
                  );
                  return updatedPreset;
                });
                onAddLocalParameter?.(e.value, index, activeParameterPreset?.value || newParameterValue);
              }}
              filter
              filterPlaceholder="Search Parameter"
              placeholder={activeParameterPreset?.name || "Select Parameter"}
              emptyMessage="No parameters available"
              showClear
              panelFooterTemplate={() => (
                <div
                  className="flex items-center gap-2 p-2 border-t bg-white border-gray-200 dark:border-gray-700 dark:bg-gray-800"
                  style={{ position: "static" }}
                >
                  <InputText
                    value={newParameterName}
                    onChange={(e) => {
                      const name = e.target.value;
                      setNewParameterName(name);
                    }}
                    placeholder="New Parameter Name"
                    className="flex-1 bg-white"
                  />
                  <IconButton
                    title="Add New Parameter"
                    additionalClassName=""
                    severity="secondary"
                    icon={<PlusIcon className="w-5 h-5 fill-white inline cursor-pointer" />}
                    onClick={() => {
                      if (!newParameterName.trim()) return;
                      // Always add to parent availableParams (so dropdowns update globally)
                      if (onAddLocalParameter) {
                        onAddLocalParameter(newParameterName, index, undefined);
                      }
                      // Select the new parameter in the dropdown
                      setActiveParameterPreset(prev => {
                        if (!prev) return prev;
                        const updatedPreset = Object.assign(
                          Object.create(Object.getPrototypeOf(prev)),
                          { ...prev, name: newParameterName, value: prev.value || "" }
                        );
                        return updatedPreset;
                      });
                      setNewParameterName("");
                    }}
                  />
                </div>
              )}
              panelClassName="min-w-full"
              disabled={!canEdit}
            />
          </div>
        ) : (
          <InputText
            className="col-span-4"
            value={activeParameterPreset?.name?.trim()}
            placeholder={activeParameterPreset?.name}
            disabled={!canEdit}
            required
          />
        )
      }
      <InputText
        className={canEdit ? "col-span-3" : "col-span-4"}
        value={activeParameterPreset?.value ?? newParameterValue}
        onChange={(e) => {
          const value = e.target.value;
          setNewParameterValue(value);
          setActiveParameterPreset(prev => {
            if (!prev) return prev;
            const updatedPreset = Object.assign(
              Object.create(Object.getPrototypeOf(prev)),
              { ...prev, value }
            );
            return updatedPreset;
          });
          onAddLocalParameter?.(activeParameterPreset?.name || newParameterName, index, value);
        }}
        placeholder={newParameterValue || activeParameterPreset?.value || "Enter Preset Value"}
        disabled={!isValueEditable}
        required
      />
      {canEdit ? (
        <div className="flex items-center space-x-1 ">
          {
            canDelete && (
              <IconButton
                title={`Delete Parameter ${index + 1}`}
                severity="danger"
                icon={<DeleteIcon className="w-4 h-4 fill-white inline cursor-pointer" />}
                onClick={() => onDelete(index)}
              />
            )
          }
          {canAdd && (
            <IconButton
              title="Add New Parameter (Local Only)"
              additionalClassName="bg-gm-black"
              severity="secondary"
              icon={<PlusIcon className="w-4 h-4 fill-white inline cursor-pointer" />}
              onClick={() => {
                const newParameter = new BasicNameValue({ name: '', value: '' });
                onAdd(newParameter);
              }}
              disabled={
                (() => {
                  // @ts-ignore
                  const key = (ParameterPresetChild as any).key || (ParameterPresetChild as any).props?.key;
                  if (typeof key === "string") {
                    const parts = key.split("-");
                    return parts[parts.length - 1] === "0";
                  }
                  return false;
                })()
              }
            />
          )}
        </div>
      ) : (<div />)}
    </>
  );
};

export default ParameterPresetChild;
